/**
 * Firebase Cloud Messaging (FCM) - register token with backend and handle foreground messages.
 * Requires firebase-messaging-sw.js at site root and (optional) REACT_APP_FIREBASE_VAPID_KEY.
 */
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { API_URL } from '@/config/api';

let _messaging = null;

function getMessagingSafe(app) {
  if (typeof window === 'undefined') return null;
  if (!_messaging && app) {
    try {
      _messaging = getMessaging(app);
    } catch (e) {
      console.warn('FCM getMessaging failed:', e);
    }
  }
  return _messaging;
}

/**
 * Request notification permission, get FCM token, and POST to backend.
 * @param {object} app - Firebase app instance
 * @param {string} authToken - JWT for API Authorization
 * @returns {Promise<string|null>} FCM token or null
 */
export async function registerFCMToken(app, authToken) {
  if (!app || !authToken || typeof window === 'undefined') return null;
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const messaging = getMessagingSafe(app);
  if (!messaging) return null;

  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || undefined;
  try {
    const token = await getToken(messaging, { vapidKey });
    if (!token) return null;
    const res = await fetch(`${API_URL}/notifications/fcm-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      console.warn('FCM token registration failed:', res.status);
      return token;
    }
    return token;
  } catch (err) {
    console.warn('FCM getToken/register failed:', err);
    return null;
  }
}

/**
 * Subscribe to foreground FCM messages (when app is open).
 * @param {object} app - Firebase app instance
 * @param {function} onPayload - callback(payload) for data/notification
 */
export function onForegroundMessage(app, onPayload) {
  if (!app || typeof window === 'undefined') return () => {};
  const messaging = getMessagingSafe(app);
  if (!messaging) return () => {};
  const unsubscribe = onMessage(messaging, (payload) => {
    if (onPayload) onPayload(payload);
  });
  return unsubscribe;
}
